import { useState } from "react"
import {
  Card,
  Upload,
  Button,
  Space,
  Typography,
  message,
  Alert,
} from "@mimber/ui-web"
import { Image, Popconfirm } from "antd"
import { UploadOutlined, DeleteOutlined, PictureOutlined } from "@mimber/ui-web"
import type { UploadFile } from "@mimber/ui-web"
import {
  useUploadOrganizationLogo,
  useDeleteOrganizationLogo,
} from "@mimber/client/mimber-organization"
import type { LogoUploadSectionProps } from "./LogoUploadSection.types"

const { Text, Paragraph } = Typography

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"]

/**
 * LogoUploadSection component for managing organization logo
 * Supports upload, preview, and delete operations with file validation
 */
export const LogoUploadSection: React.FunctionComponent<
  LogoUploadSectionProps
> = ({ organization, onSuccess, disabled = false }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const uploadLogo = useUploadOrganizationLogo(organization.uuid)
  const deleteLogo = useDeleteOrganizationLogo(organization.uuid)

  const beforeUpload = (file: File) => {
    const isValidType = ALLOWED_TYPES.includes(file.type)
    if (!isValidType) {
      message.error("You can only upload PNG, JPG, or SVG files!")
      return Upload.LIST_IGNORE
    }

    const isValidSize = file.size <= MAX_FILE_SIZE
    if (!isValidSize) {
      message.error("File must be smaller than 5MB!")
      return Upload.LIST_IGNORE
    }

    return false // Prevent auto upload, we'll handle it manually
  }

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning("Please select a file first")
      return
    }

    const file = fileList[0].originFileObj as File

    uploadLogo.mutate(
      { file },
      {
        onSuccess: () => {
          message.success("Logo uploaded successfully")
          setFileList([])
          onSuccess?.()
        },
        onError: (error) => {
          message.error(error.message || "Failed to upload logo")
        },
      }
    )
  }

  const handleDelete = () => {
    deleteLogo.mutate(undefined, {
      onSuccess: () => {
        message.success("Logo deleted successfully")
        onSuccess?.()
      },
      onError: (error) => {
        message.error(error.message || "Failed to delete logo")
      },
    })
  }

  const handleChange = (info: { fileList: UploadFile[] }) => {
    // Only keep the most recent file
    setFileList(info.fileList.slice(-1))
  }

  return (
    <Card
      title={
        <Space>
          <PictureOutlined />
          <span>Organization Logo</span>
        </Space>
      }
    >
      <Paragraph type="secondary">
        Upload your organization's logo. This will be displayed on your public
        profile and throughout the member app. Supported formats: PNG, JPG, SVG
        (max 5MB).
      </Paragraph>

      {/* Current Logo Display */}
      {organization.logoStorageItem?.storageUrl && (
        <div style={{ marginBottom: 24 }} data-testid="current-logo-section">
          <Text strong>Current Logo:</Text>
          <div style={{ marginTop: 8 }}>
            <Image
              src={organization.logoStorageItem.storageUrl}
              alt={`${organization.name} logo`}
              width={200}
              height={200}
              style={{ objectFit: "contain" }}
              placeholder
              data-testid="current-logo-image"
            />
          </div>
          <div style={{ marginTop: 8 }}>
            <Popconfirm
              title="Delete Logo"
              description="Are you sure you want to delete the current logo?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deleteLogo.isPending}
                disabled={disabled}
                data-testid="delete-logo-button"
              >
                Delete Logo
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {/* Upload Section */}
      <Space orientation="vertical" style={{ width: "100%" }}>
        <Alert
          message="Requirements"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Formats: PNG, JPG, SVG</li>
              <li>Maximum size: 5MB</li>
              <li>Recommended: Square aspect ratio (e.g., 500x500px)</li>
              <li>High resolution for best quality</li>
            </ul>
          }
          type="info"
          showIcon
        />

        <Upload.Dragger
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          maxCount={1}
          disabled={disabled || uploadLogo.isPending}
          accept=".png,.jpg,.jpeg,.svg"
          data-testid="upload-dragger"
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for single file upload. Replace existing logo by uploading a
            new one.
          </p>
        </Upload.Dragger>

        {fileList.length > 0 && (
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploadLogo.isPending}
            disabled={disabled}
            icon={<UploadOutlined />}
            block
            data-testid="upload-logo-button"
          >
            Upload Logo
          </Button>
        )}
      </Space>
    </Card>
  )
}
